import { useEffect, useRef, useState } from 'react';
import './SentimentWidget.css';
import {
  BaseWidgetProps,
  WidgetError,
  WidgetHeightMessage,
  WidgetMessage,
} from '../../types/common.type';
import useResizeNotifier from '../../hooks/useResizeNotifier';
import {
  ThemeType,
  WidgetMessageType,
  WidgetNameSource,
} from '../../utils/type';
import SentimentGaugeChart from './components/SentimentGaugeChart';
import SentimentBarChart from './components/SentimentBarChart';
import SentimentTable from './components/SentimentTable';
import Layout from '../../Components/Common/Layout/Layout';
import InfoPanel from '../../Components/Common/InfoPanel/InfoPanel';
import useSentiment from '../../hooks/data/useSentiment';
import Loading from '../../Components/Loading/Loading';
import ErrorMessage from '../../Components/ErrorMessage/ErrorMessage';
import { errorMsg } from '../../constants/common.constant';
import { SentimentDays } from '../../types/sentiment.type';
import { formatDayMonthShort } from '../../helpers/date';
import { ReactComponent as SentimentInfoIcon } from '../../assets/icons/tooltip-company-score-icon.svg';

export interface SentimentWidgetProps extends BaseWidgetProps {
  token?: string;
  symbol?: string;
  onHeightChange?: (message: WidgetHeightMessage) => void;
  onError?: (error: WidgetError) => void;
  onNoData?: (message: WidgetMessage) => void;
}

export const PERIOD_OPTIONS: {
  label: string;
  shortLabel: string;
  value: SentimentDays;
}[] = [
  { label: '24 hrs', shortLabel: '24H', value: '1' },
  { label: '7 days', shortLabel: '7D', value: '7' },
  { label: '30 days', shortLabel: '30D', value: '30' },
  { label: '90 days', shortLabel: '90D', value: '90' },
];

const SENTIMENT_INFO_TITLE = 'About Sentiment';

const SENTIMENT_INFO_CONTENT = (
  <div className="sentiment-info-text">
    <p>
      LSEG MarketPsych analyses news and social media using natural language
      processing to identify sentiment about a company. Sentiment scores reflect
      the balance of positive and negative language, normalised by the volume of
      relevant references. Scores range from −1 (most negative) to +1 (most
      positive), with values near zero indicating neutral or balanced sentiment.
      The widget shows sentiment over the selected period. Media Buzz measures
      the volume of relevant discussion; taller bars indicate more activity.
      High buzz can accompany either positive or negative sentiment.
    </p>
    <p>
      Note: This score is derived solely from news and social-media coverage and
      may differ from professional broker or analyst ratings, which are based on
      fundamental research and formal recommendations.
    </p>
  </div>
);

const SentimentWidget = (props: SentimentWidgetProps) => {
  const {
    symbol,
    token,
    onHeightChange,
    onError,
    onNoData,
    onLoadingChange,
    theme = ThemeType.Direct,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const { data, isLoading, error } = useSentiment(
    symbol,
    token,
    onError,
    onNoData,
  );
  useResizeNotifier(containerRef, WidgetNameSource.Sentiment, onHeightChange);
  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [isLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedPeriod, setSelectedPeriod] = useState<SentimentDays>();
  const [isInfoOpen, setIsInfoOpen] = useState(false);

  useEffect(() => {
    if (!isInfoOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsInfoOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isInfoOpen]);

  const onNoDataRef = useRef(onNoData);
  onNoDataRef.current = onNoData;
  useEffect(() => {
    if (!isLoading && Boolean(error)) {
      onNoDataRef.current?.({
        type: WidgetMessageType.NoData,
        source: 'SentimentWidget',
        data: null,
      });
    }
  }, [isLoading, error]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!data) return;

    setSelectedPeriod(() => {
      return Object.keys(data).find(
        (key) => data[key as SentimentDays]?.overall.sentiment,
      ) as SentimentDays;
    });
  }, [data]);
  const msgItem = errorMsg.find((obj) => obj.key === 'sentiment');

  return (
    <Layout theme={theme}>
      <div ref={containerRef} className="sentiment-container">
        {isLoading && !data && !onLoadingChange ? (
          <Loading />
        ) : !isLoading && error ? (
          <ErrorMessage msg={msgItem} />
        ) : (
          <div className="sentiment-body">
            {isInfoOpen && (
              <InfoPanel
                title={SENTIMENT_INFO_TITLE}
                onClose={() => setIsInfoOpen(false)}
              >
                {SENTIMENT_INFO_CONTENT}
              </InfoPanel>
            )}
            <div className="sentiment-chart-container">
              <div className="sentiment-period-group" role="tablist">
                {PERIOD_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    role="tab"
                    aria-selected={selectedPeriod === opt.value}
                    disabled={!data?.[opt.value]?.overall.sentiment}
                    className={`sentiment-period-option${selectedPeriod === opt.value ? ' active' : ''}`}
                    onClick={() => setSelectedPeriod(opt.value)}
                  >
                    <span className="sentiment-period-label--full">
                      {opt.label}
                    </span>
                    <span className="sentiment-period-label--short">
                      {opt.shortLabel}
                    </span>
                  </button>
                ))}
              </div>
              {selectedPeriod && (
                <>
                  <div className="sentiment-gauge-wrapper">
                    <SentimentGaugeChart
                      value={Number(data?.[selectedPeriod]?.overall.sentiment)}
                    />
                    <button
                      type="button"
                      className="sentiment-info-button"
                      aria-label={SENTIMENT_INFO_TITLE}
                      onClick={() => setIsInfoOpen(true)}
                    >
                      <SentimentInfoIcon />
                    </button>
                  </div>
                  <div className="sentiment-bar-wrapper">
                    <SentimentBarChart
                      data={data?.[selectedPeriod]?.datalist || []}
                      period={selectedPeriod}
                    />
                  </div>
                </>
              )}
              <div className="sentiment-charts-caption">Media Buzz</div>
            </div>
            <SentimentTable
              data={data!}
              latestScoreDate={
                data?.[selectedPeriod!]?.overall.lastTs
                  ? formatDayMonthShort(data[selectedPeriod!]!.overall.lastTs)
                  : undefined
              }
            />
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SentimentWidget;
